import React, { useState, useRef, useEffect } from 'react';
import { View, StyleSheet, Dimensions, Image, TouchableOpacity, Text, Linking, Platform } from 'react-native';
import { getViewportDimensions } from '../../../../Config/Const';
import ArrowButton from '../../../Common/ArrowButton';
import { useBanners } from '../../../BannerManager';
import PlatformDetector from '../../../../utils/PlatformDetector';
import CarouselContainer from '../../../Common/CarouselContainer';
import { globalStyles, getContentPadding } from '../../../../styles/globalStyles';

// Importar CSS para prevenir scroll horizontal en web
if (Platform.OS === 'web') {
  // Importar el CSS solo en entorno web
  try {
    // @ts-ignore
    require('../../../../styles/preventHorizontalScroll.css');
  } catch (e) {
    console.warn('No se pudo cargar el CSS para prevenir scroll horizontal');
  }
}

// Aspect ratio del banner desktop (ancho:alto) - ajustar según tus imágenes
// Ratio aproximado del banner proporcionado (ancho:alto) ~ 1024x312 => 3.28:1
const BANNER_DESKTOP_ASPECT_RATIO = 3.28;

// Función para calcular altura responsiva del banner principal según ancho de pantalla
const getResponsiveBannerHeight = (width, viewportHeight = null) => {
  // En móvil: usar 70% del alto de la pantalla
  if (width < 768 && viewportHeight) {
    return viewportHeight * 0.7;
  }
  // Fallback para móvil sin viewportHeight
  if (width < 400) return 200;
  if (width < 576) return 280;
  if (width < 768) return 350;
  // Desktop y tablet: altura calculada según aspect ratio para eliminar espacio vacío
  return width / BANNER_DESKTOP_ASPECT_RATIO;
};

// Importamos getViewportDimensions desde Config/Const.js
const { width: initialWidth } = getViewportDimensions();
const initialViewportHeight = Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerHeight : 800;
const CAROUSEL_WIDTH = initialWidth;
const CAROUSEL_HEIGHT = getResponsiveBannerHeight(initialWidth, initialViewportHeight);

const BannerPrincipal = () => {
  const { banners: allBanners, loading, error, isWeb } = useBanners();
  const [currentPage, setCurrentPage] = useState(0);
  const scrollViewRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: initialWidth,
    height: CAROUSEL_HEIGHT,
    viewportHeight: initialViewportHeight
  });
  // Obtener los banners de la fila 2 (BannerPrincipal)
  const fila2Banners = allBanners.fila2 || [];
  // Determinar si estamos en modo móvil (ya sea nativo o web responsive)
  const isMobileView = PlatformDetector.isMobile() || (Platform.OS === 'web' && dimensions.width < 768);
  const isMobileWeb = Platform.OS === 'web' && dimensions.width < 768;
  // Usar altura responsiva calculada (con viewportHeight para móvil)
  const bannerHeight = getResponsiveBannerHeight(dimensions.width, dimensions.viewportHeight);
  
  // Filtrar banners según la plataforma actual
  const filteredBanners = fila2Banners.filter(banner => {
    // En modo móvil (nativo o web responsive): requerir imagen mobile
    // En modo desktop web: requerir imagen web
    return isMobileView ? !!banner.imagenMobile : !!banner.imagenWeb;
  });
  
  // Verificar si debemos mostrar el componente (solo si hay banners filtrados o estamos cargando)
  const shouldRender = (filteredBanners.length > 0) || loading;
  
  // Usar los banners filtrados si están disponibles, de lo contrario usar los predeterminados solo durante la carga
  const originalBanners = filteredBanners.length > 0 
    ? filteredBanners
        .map(banner => ({
          id: banner.codBanner.toString(),
          posicion: banner.posicion || 999, // Si no tiene posición, ponerlo al final
          image: isMobileView ? banner.imagenMobile : banner.imagenWeb, // Usar imagen según el modo (móvil o desktop)
          link: banner.link,
          titulo: banner.titulo
        }))
        .sort((a, b) => a.posicion - b.posicion) // Ordenar por posición
    : loading ? [] : [];

  // Usar los banners originales sin duplicados fantasma
  const banners = originalBanners;

  // Función para actualizar la URL con el parámetro codBanner del banner actual
  // Esta función ahora está desactivada para evitar cambios automáticos en la URL
  const updateUrlWithCurrentBanner = (pageIndex) => {
    // Desactivamos la actualización automática de URL para evitar redirecciones no deseadas
    // Si se necesita reactivar esta funcionalidad en el futuro, simplemente descomenta el código
    
    /* CÓDIGO DESACTIVADO
    // Solo actualizar en plataforma web y cuando no sea un banner fantasma
    if (Platform.OS === 'web' && typeof window !== 'undefined' && pageIndex > 0 && pageIndex < banners.length - 1) {
      // El banner real está en la posición pageIndex (ajustado por los banners fantasma)
      const currentBanner = banners[pageIndex];
      
      // Si es un banner válido y no es un banner fantasma
      if (currentBanner && currentBanner.id !== 'first' && currentBanner.id !== 'last') {
        // Construir URL con parámetros
        const urlParams = new URLSearchParams();
        
        // Siempre incluir D, C, S como 0 (por defecto)
        urlParams.append('D', 0);
        urlParams.append('C', 0);
        urlParams.append('S', 0);
        
        // Agregar codBanner usando el ID del banner
        urlParams.append('codBanner', currentBanner.id);
        urlParams.append('searchText', '');
        
        const url = `/productos?${urlParams.toString()}`;
        
        // Navegar usando history API sin disparar evento (para evitar recargas)
        window.history.replaceState({}, '', url);
      }
    }
    */
  };

  const handleScroll = (event) => {
    const contentOffset = event.nativeEvent.contentOffset;
    const contentPadding = Platform.OS === 'web' ? getContentPadding(dimensions.width) : 0;
    const contentWidth = Platform.OS === 'web' ? Math.max(0, dimensions.width - 2 * contentPadding) : dimensions.width;
    const page = Math.round(contentOffset.x / contentWidth);
    
    if (!isScrolling) {
      setCurrentPage(page);
      updateUrlWithCurrentBanner(page);
    }
  };

  const scrollToNextPage = () => {
    setIsScrolling(true);
    let nextPage = currentPage + 1;
    
    // Si llegamos al final, volver al principio (carrusel infinito)
    if (nextPage >= banners.length) {
      nextPage = 0;
    }
    
    const contentPadding = Platform.OS === 'web' ? getContentPadding(dimensions.width) : 0;
    const contentWidth = Platform.OS === 'web' ? Math.max(0, dimensions.width - 2 * contentPadding) : dimensions.width;
    scrollViewRef.current?.scrollTo({
      x: nextPage * contentWidth,
      animated: true
    });
    setCurrentPage(nextPage);
    
    // Actualizar URL con el banner correspondiente
    updateUrlWithCurrentBanner(nextPage);
    
    setIsScrolling(false);
  };

  const scrollToPrevPage = () => {
    setIsScrolling(true);
    let prevPage = currentPage - 1;
    
    // Si estamos al principio, ir al final (carrusel infinito)
    if (prevPage < 0) {
      prevPage = banners.length - 1;
    }
    
    const contentPadding = Platform.OS === 'web' ? getContentPadding(dimensions.width) : 0;
    const contentWidth = Platform.OS === 'web' ? Math.max(0, dimensions.width - 2 * contentPadding) : dimensions.width;
    scrollViewRef.current?.scrollTo({
      x: prevPage * contentWidth,
      animated: true
    });
    setCurrentPage(prevPage);
    
    // Actualizar URL con el banner correspondiente
    updateUrlWithCurrentBanner(prevPage);
    
    setIsScrolling(false);
  };

  useEffect(() => {
    // Solo inicializar cuando los banners estén disponibles
    if (banners.length > 0) {
      // Inicializar siempre en el primer banner (posición 0)
      scrollViewRef.current?.scrollTo({
        x: 0,
        animated: false
      });
      setCurrentPage(0);
    }
  }, [banners.length]);
  
  // Efecto separado para el auto-scroll
  // Efecto para manejar el cambio de tamaño de la ventana (solo en web)
  useEffect(() => {
    if (PlatformDetector.isWeb()) {
      const handleResize = () => {
        const newWidth = window.innerWidth;
        const newViewportHeight = window.innerHeight;
        const newHeight = getResponsiveBannerHeight(newWidth, newViewportHeight);
        const newDimensions = { width: newWidth, height: newHeight, viewportHeight: newViewportHeight };
        setDimensions(newDimensions);
        
        // Ajustar la posición del scroll al cambiar el tamaño
        if (scrollViewRef.current && currentPage > 0) {
          const pad = Platform.OS === 'web' ? getContentPadding(newDimensions.width) : 0;
          const contentW = Platform.OS === 'web' ? Math.max(0, newDimensions.width - 2 * pad) : newDimensions.width;
          scrollViewRef.current.scrollTo({
            x: currentPage * contentW,
            animated: false
          });
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [currentPage]);

  useEffect(() => {
    // Solo hacer auto-scroll si hay más de un banner
    if (banners.length > 1) {
      // Temporizador de 5 segundos por imagen
      const interval = setInterval(() => {
        let nextPage = currentPage + 1;
        
        // Si llegamos al final, volver al principio
        if (nextPage >= banners.length) {
          nextPage = 0;
        }
        
        const pad = Platform.OS === 'web' ? getContentPadding(dimensions.width) : 0;
        const contentW = Platform.OS === 'web' ? Math.max(0, dimensions.width - 2 * pad) : dimensions.width;
        scrollViewRef.current?.scrollTo({
          x: nextPage * contentW,
          animated: true
        });
        setCurrentPage(nextPage);
        
        // Actualizar URL con el banner correspondiente
        updateUrlWithCurrentBanner(nextPage);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [banners.length, currentPage, dimensions.width]);

  // Solo renderizar si hay banners para mostrar o si estamos cargando
  if (!shouldRender) {
    return null;
  }

  const pad = Platform.OS === 'web' ? getContentPadding(dimensions.width) : 0;
  const contentW = Platform.OS === 'web' ? Math.max(0, dimensions.width - 2 * pad) : dimensions.width;
  const bannerHeightDisplay = getResponsiveBannerHeight(contentW, dimensions.viewportHeight);
  return (
    <View style={[styles.carouselContainer, { height: bannerHeightDisplay }]}>
      {originalBanners.length > 1 && (
        <ArrowButton direction="left" onPress={scrollToPrevPage} />
      )}

      {originalBanners.length > 1 && (
        <ArrowButton direction="right" onPress={scrollToNextPage} />
      )}

      <CarouselContainer
        ref={scrollViewRef}
        pagingEnabled={true}
        snapToInterval={contentW}
        onMomentumScrollEnd={handleScroll}
        contentContainerStyle={styles.scrollViewContent}
      >
        {banners.map((banner) => {
          // Obtener el banner original para acceder al campo apertura
          const originalBanner = fila2Banners.find(b => b.codBanner.toString() === banner.id);
          const abrirEnPestañaNueva = originalBanner?.apertura === 1;
          
          return (
          <TouchableOpacity 
            key={banner.id} 
            style={[styles.banner, { width: contentW, height: bannerHeightDisplay }]}
            onPress={() => {
              // Si no es un banner fantasma (first o last)
              if (banner.id !== 'first' && banner.id !== 'last') {
                const isDesktop = dimensions.width >= 600;
                
                // Si el banner tiene un link definido, redirigir a ese link
                if (banner.link && banner.link.trim() !== '') {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    // Verificar si es una URL externa completa (http:// o https://)
                    const isExternalUrl = banner.link.startsWith('http://') || banner.link.startsWith('https://');
                    
                    let finalUrl;
                    if (isExternalUrl) {
                      // Si es una URL externa completa, usarla directamente
                      finalUrl = banner.link;
                    } else {
                      // Si es una ruta relativa o contiene pinguino.com.ar, extraer la ruta
                      const linkUrl = banner.link.includes('pinguino.com.ar') 
                        ? banner.link.split('pinguino.com.ar')[1] 
                        : banner.link;
                      finalUrl = window.location.origin + linkUrl;
                    }
                    
                    // Si apertura es 0, abrir en pestaña nueva (solo en desktop)
                    if (abrirEnPestañaNueva && isDesktop) {
                      window.open(finalUrl, '_blank', 'noopener,noreferrer');
                      return;
                    }
                    
                    // Si apertura es 1 o estamos en móvil, navegar en la misma pestaña
                    if (isExternalUrl) {
                      // Si es URL externa, abrir directamente
                      window.location.href = finalUrl;
                    } else {
                      // Si es ruta interna, verificar si ya estamos en /productos
                      const isOnProductsPage = window.location.pathname === '/productos';
                      
                      if (isOnProductsPage) {
                        // Si ya estamos en productos, solo actualizar la URL y disparar evento
                        window.history.pushState({}, '', finalUrl.replace(window.location.origin, ''));
                        window.dispatchEvent(new Event('navigationChange'));
                      } else {
                        // Si estamos en otra página, navegar usando window.location
                        window.location.href = finalUrl;
                      }
                    }
                  } else {
                    // En dispositivos móviles nativos, usar el enlace directo
                    Linking.openURL(banner.link);
                  }
                  return;
                }

                // Si no hay link, usar la lógica actual de navegación
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  // Construir URL con parámetros - siempre incluir todos los parámetros
                  const urlParams = new URLSearchParams();
                  
                  // Siempre incluir D, C, S como 0 (por defecto)
                  urlParams.append('D', 0);
                  urlParams.append('C', 0);
                  urlParams.append('S', 0);
                  
                  // Agregar codBanner usando el ID del banner
                  urlParams.append('codBanner', banner.id);
                  urlParams.append('searchText', '');
                  
                  const url = `/productos?${urlParams.toString()}`;
                  
                  // Si apertura es 0, abrir en pestaña nueva (solo en desktop)
                  if (abrirEnPestañaNueva && isDesktop) {
                    const fullUrl = window.location.origin + url;
                    window.open(fullUrl, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  
                  // Si apertura es 1 o estamos en móvil, navegar en la misma pestaña
                  const isOnProductsPage = window.location.pathname === '/productos';
                  
                  if (isOnProductsPage) {
                    // Si ya estamos en productos, solo actualizar la URL y disparar evento
                    window.history.pushState({}, '', url);
                    window.dispatchEvent(new Event('navigationChange'));
                  } else {
                    // Si estamos en otra página, navegar usando window.location
                    window.location.href = url;
                  }
                }
              }
            }}
            activeOpacity={banner.link ? 0.8 : 1}
          >
            {typeof banner.image === 'string' ? (
              <Image
                source={{ uri: banner.image }}
                style={styles.bannerImage}
                resizeMode="contain"
              />
            ) : (
              <Image
                source={banner.image}
                style={styles.bannerImage}
                resizeMode="contain"
              />
            )}
          </TouchableOpacity>
        );
        })}
      </CarouselContainer>
      {banners.length > 1 && (
        <View style={styles.pagination}>
          {banners.map((_, index) => (
            <View
              key={index}
              style={[styles.dot, { backgroundColor: index === currentPage ? '#fff' : 'rgba(255,255,255,0.5)' }]}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  scrollViewContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowButton: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -20 }],
    width: 40,
    height: 40,
    backgroundColor: 'rgba(252, 1, 1, 0.5)',
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  leftArrow: {
    left: 10,
  },
  rightArrow: {
    right: 10,
  },
  arrowText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
  },
  carouselContainer: {
    position: 'relative',
    width: '100%',
    // La altura ahora se establece dinámicamente en el componente
    marginBottom: 40,
    alignSelf: 'center',
    overflow: 'hidden',
    // Sin maxHeight fijo para que se adapte al aspect ratio
    // El contenedor padre ya aplica el padding lateral
  },
  banner: {
    // El ancho y alto ahora se establecen dinámicamente en el componente
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    marginHorizontal: 0,
  },
  bannerImage: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' ? {
      objectFit: 'contain',
    } : {}),
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    position: 'absolute',
    bottom: 10, // Ajustado para el nuevo tamaño
    width: '100%',
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginHorizontal: 5,
  },
});

export default BannerPrincipal;
